import React, {useCallback, useEffect, useState} from 'react';
import {Button, Typography, Divider} from 'antd';
import {InboxOutlined, UploadOutlined} from '@ant-design/icons';
import {ModelDTO} from 'hemwb-api';
import {ModelTreeNode} from '../../../../store/modelFile';
import {trackFilesTabEvent} from '../../../../tracking/tracking';
import {tid} from '../../../../testUtils';
import ModelEditFilesAntd from '../../model/common/ModelEdit/ModelEditFiles/ModelEditFilesAntd';
import {FileManagerAntdHooks} from '../../model/common/ModelEdit/ModelEditFiles/FileManagerAntd';
import styles from './StepFilesRedesigned.module.scss';

const {Text} = Typography;

interface StepFilesRedesignedProps {
  model: ModelDTO;
  files?: ModelTreeNode[];
  onChange?: (updatedModel: ModelDTO, updatedFiles: ModelTreeNode[]) => void;
  onSubmit?: (updatedModel: ModelDTO, updatedFiles: ModelTreeNode[]) => void;
  onBack?: () => void;
  showInstructions?: boolean;
  buttons?: (params: {
    backText?: string;
    onNext?: () => void;
    onBack?: () => void;
    disableNext?: boolean;
    showBack?: boolean;
  }) => React.ReactNode;
}

const StepFilesRedesigned: React.FC<StepFilesRedesignedProps> = ({
  model,
  files,
  onChange,
  onSubmit,
  onBack,
  showInstructions = true,
  buttons,
}) => {
  const [currentNodes, setCurrentNodes] = useState<ModelTreeNode[]>([]);

  useEffect(() => {
    trackFilesTabEvent();
  }, []);

  const handleSubmit = useCallback(() => {
    if (onSubmit) {
      onSubmit(model, currentNodes);
    }
  }, [model, currentNodes, onSubmit]);

  const handleChange = useCallback(
    (updatedModel: ModelDTO, updatedFiles: ModelTreeNode[]) => {
      setCurrentNodes(updatedFiles);
      onChange?.(updatedModel, updatedFiles);
    },
    [onChange],
  );

  const renderCustomUploadArea = useCallback((hooks: FileManagerAntdHooks) => {
    const {
      fileInputRef,
      folderInputRef,
      isDirUploadAvailable,
      handleAddFiles,
      handleDrop,
      nodes,
      selectedCount,
      renderFileTagSelection,
      renderFileTree,
    } = hooks;

    const handleDropZoneDrop = (event: React.DragEvent) => {
      const rootNode = {uuid: '', path: '', name: '', folder: true, uploaded: true};
      handleDrop(event, rootNode);
    };

    return (
      <>
        <div className={styles['drop-zone']} onDrop={handleDropZoneDrop} onDragOver={(e) => e.preventDefault()}>
          <InboxOutlined style={{fontSize: '48px', color: '#006A63', marginBottom: '16px'}} />
          <Text style={{fontSize: '16px', display: 'block', marginBottom: '8px', color: '#595959'}}>
            Click or drag file to this area to upload
          </Text>
          <Text type="secondary" style={{display: 'block', marginBottom: '20px'}}>
            Support for a single or bulk upload.
          </Text>
          <div style={{display: 'flex', flexDirection: 'column', gap: '12px', alignItems: 'center'}}>
            <Button
              type="primary"
              icon={<UploadOutlined />}
              size="large"
              style={{
                background: '#006A63',
                borderColor: '#006A63',
                height: '40px',
                width: '280px',
                fontSize: '14px',
                fontWeight: 500,
              }}
              onClick={() => fileInputRef.current?.click()}
              {...tid('btn-select-files')}
            >
              Upload files from your computer
            </Button>
            {isDirUploadAvailable && (
              <Button
                type="default"
                icon={<UploadOutlined />}
                size="large"
                style={{
                  height: '40px',
                  width: '280px',
                  fontSize: '14px',
                  fontWeight: 500,
                  borderColor: '#d9d9d9',
                  color: '#000000',
                }}
                onClick={() => folderInputRef.current?.click()}
                {...tid('btn-select-folder')}
              >
                Upload folder from your computer
              </Button>
            )}
          </div>
        </div>

        <input
          type="file"
          multiple
          onChange={handleAddFiles}
          ref={fileInputRef}
          style={{display: 'none'}}
          {...tid('input-file')}
        />
        {isDirUploadAvailable && (
          <input
            type="file"
            {...({webkitdirectory: 'webkitdirectory'} as any)}
            onChange={handleAddFiles}
            ref={folderInputRef}
            style={{display: 'none'}}
            {...tid('input-folder')}
          />
        )}

        {selectedCount > 1 && <div style={{marginTop: '24px'}}>{renderFileTagSelection()}</div>}

        {nodes && nodes.length > 0 && (
          <div className={styles['files-container']} style={{marginTop: '24px'}}>
            {renderFileTree()}
          </div>
        )}
      </>
    );
  }, []);

  return (
    <div className={styles['step-files-redesigned']}>
      <div className={styles['step-files-content']}>
        {showInstructions && (
          <div className={styles['upload-instructions']}>
            <div className={styles['instructions-header']}>
              <h2 className={styles['instructions-title']}>Upload your files</h2>
              <p className={styles['instructions-subtitle']}>
                Get started by uploading your model files from your computer
              </p>
            </div>
            <div className={styles['instruction-items-container']}>
              <div className={styles['instruction-item']}>
                <span className={`${styles['instruction-number']} ${styles['instruction-number']}`}>1</span>
                <div>
                  <span className={styles['instruction-title']}>Upload files from your computer</span>
                  <span className={styles['instruction-description']}>
                    Use "Select files" for individual files or "Select folders" for entire directories
                  </span>
                </div>
              </div>
              <div className={styles['instruction-item']}>
                <span className={styles['instruction-number']}>2</span>
                <div>
                  <span className={styles['instruction-title']}>Drag and drop alternative</span>
                  <span className={styles['instruction-description']}>
                    Simply drag files from your computer directly into the upload area
                  </span>
                </div>
              </div>
              <div className={styles['instruction-item']}>
                <span className={styles['instruction-number']}>3</span>
                <div>
                  <span className={styles['instruction-title']}>Review and proceed</span>
                  <span className={styles['instruction-description']}>
                    Check your uploaded files and continue to add metadata
                  </span>
                </div>
              </div>
            </div>
            <Divider style={{margin: '4px 0', borderColor: '#e8e8e8'}} />
            <div className={styles['supported-files-info']}>
              <span className={styles['supported-files-text']}>
                Supported file types: Excel models (.xlsx, .xls), Python scripts (.py), R scripts (.R), documentation
                (.pdf, .docx), Mathematica scripts (.wls, .nb) and more
              </span>
            </div>
          </div>
        )}

        {/* Right side - File Manager */}
        <div className={styles['upload-area-container']}>
          <ModelEditFilesAntd
            model={model}
            files={files}
            onChange={handleChange}
            onSubmit={(updatedModel, updatedFiles) => {
              onSubmit?.(updatedModel, updatedFiles);
            }}
            buttons={() => null}
            uploadFlow={true}
            customUploadArea={renderCustomUploadArea}
          />
        </div>
      </div>

      {/* Footer buttons */}
      {buttons && buttons({backText: 'Cancel', onNext: handleSubmit, onBack: onBack, showBack: !!onBack})}
    </div>
  );
};

export default StepFilesRedesigned;
